import { IPosition, IPositionType } from 'src/generated-types'
import { IChangeType } from 'src/generated-types/project-version-changes'
import { ConfigurePalette } from '../components/positions/configure/configurePalette'
import { addHitLayers, styleNode } from '../components/positions/configure/drawingSvgPainter'
import { markPosition, SVG_NAMESPACE, tagOf } from '../components/positions/configure/drawingSvgElements'
import { ACTION_ATTRIBUTE } from '../components/positions/configure/useDrawingSelection'
import { appendBadge, drawChangeBadges, drawMoveGhost, OTHER_TYPE_OPACITY, pointOf } from './revisionMapOverlay'

const REMOVED_OPACITY = '0.6'
const REMOVED_DASH = '6 4'

// Set on the clickable layers of a removed position's ghost, with the position's name.
export const RESTORE_ATTRIBUTE = 'data-revision-restore'

interface RevisionPaintOptions {
  // Positions of the draft; unchanged ones keep the look the dialog gave them.
  positions: IPosition[]
  changeByName: Map<string, IChangeType>
  // Published names of the positions the draft removed.
  removedNames: Set<string>
  colors: Record<IChangeType, string>
  palette: ConfigurePalette
  // The published map and its positions, used to draw positions the draft removed or moved.
  base?: { svg: string; positions: IPosition[] }
  // Only positions of this type are coloured; selected ones keep their selection look.
  activeType: IPositionType
  skipIds: Set<number>
  // Names of removed positions the user picked; their ghosts get the selection look.
  selectedRemoved: Set<string>
  movedNames: Set<string>
  ghostColor: string
  // The name a draft position has in the published version, which differs for a renamed one.
  publishedNameOf: (name?: string) => string
}

const isStrokeShape = (element: SVGElement) => {
  const tag = tagOf(element)
  const fill = element.getAttribute('fill')
  return tag === 'line' || tag === 'rect' || (tag === 'path' && (!fill || fill === 'none'))
}

const parseSvg = (markup: string) => {
  const holder = document.createElement('div')
  holder.innerHTML = markup
  return holder.querySelector('svg')
}

const withDescendants = (nodes: Element[]) => [
  ...nodes,
  ...nodes.flatMap(node => Array.from(node.querySelectorAll('*'))),
]

// The map's own hit layers stay in charge of selection, so copied nodes must not react to the pointer.
const makeInert = (clones: SVGElement[]) =>
  clones.forEach(clone => {
    clone.setAttribute('pointer-events', 'none')
    ;[clone, ...Array.from(clone.querySelectorAll('[data-cfg-id]'))].forEach(node => {
      node.removeAttribute('data-cfg-id')
      node.removeAttribute('data-cfg-type')
    })
  })

// Clicking a removed position's ghost restores it; the hit layers carry its name instead of an id the dialog would select.
const makeRestorable = (svg: SVGSVGElement, clones: SVGElement[], position: IPosition, palette: ConfigurePalette) => {
  withDescendants(clones).forEach(node => addHitLayers(node as SVGElement, position, false, palette.primary))
  svg.querySelectorAll(`[data-cfg-id="${position.id}"]`).forEach(layer => {
    layer.setAttribute(RESTORE_ATTRIBUTE, position.name ?? '')
    layer.setAttribute(ACTION_ATTRIBUTE, '1')
    layer.removeAttribute('data-cfg-id')
    layer.removeAttribute('data-cfg-type')
  })
}

// The published map is laid out from its own corners, so a grid grown to the left or bottom shifts the draft's map.
// Copies taken from the published map are moved by the offset between a corner both maps share.
const offsetFromBase = (
  svg: SVGSVGElement,
  baseSvg: SVGSVGElement,
  positions: IPosition[],
  basePositions: IPosition[],
  publishedNameOf: (name?: string) => string,
) => {
  const draftCornerIds = new Map(
    positions.filter(p => p.type === IPositionType.Corner).map(p => [publishedNameOf(p.name), p.id] as const),
  )
  const coordinate = (element: Element, name: string) => Number(element.getAttribute(name))
  for (const corner of basePositions.filter(p => p.type === IPositionType.Corner)) {
    const draftId = draftCornerIds.get(corner.name ?? '')
    const from = draftId != null ? baseSvg.querySelector(`#positionId-${corner.id}`) : null
    const to = draftId != null ? svg.querySelector(`#positionId-${draftId}`) : null
    if (from && to)
      return { x: coordinate(to, 'x') - coordinate(from, 'x'), y: coordinate(to, 'y') - coordinate(from, 'y') }
  }
  return { x: 0, y: 0 }
}

// Removed positions are not in the draft's map, so they are copied over from the published one.
const overlayRemoved = (
  svg: SVGSVGElement,
  baseSvg: SVGSVGElement,
  basePositions: IPosition[],
  removedNames: Set<string>,
  options: RevisionPaintOptions,
  offset: { x: number; y: number },
) => {
  const color = options.colors[IChangeType.Removed]
  const layer = svg.appendChild(document.createElementNS(SVG_NAMESPACE, 'g'))
  layer.setAttribute('transform', `translate(${offset.x} ${offset.y})`)
  basePositions
    .filter(position => position.type !== IPositionType.Cage && removedNames.has(position.name ?? ''))
    .forEach(position => {
      const isActive = position.type === options.activeType
      const picked = isActive && options.selectedRemoved.has(position.name ?? '')
      const nodes = markPosition(baseSvg, position)
      const hasLabelBox = nodes.some(node => tagOf(node) === 'rect')
      // A text node already carries its textPath child, so only outermost nodes are copied.
      const clones = nodes
        .filter(node => !nodes.some(other => other !== node && other.contains(node)))
        .map(node => layer.appendChild(node.cloneNode(true)) as SVGElement)

      clones.forEach(clone => {
        styleNode(clone, picked ? 'selected' : 'configured', color, options.palette, hasLabelBox)
        clone.setAttribute('opacity', picked ? '1' : isActive ? REMOVED_OPACITY : OTHER_TYPE_OPACITY)
        if (isStrokeShape(clone)) clone.setAttribute('stroke-dasharray', REMOVED_DASH)
      })
      makeInert(clones)
      if (isActive) makeRestorable(svg, clones, position, options.palette)

      const point = pointOf(withDescendants(clones), position.type)
      if (!point) return
      const target = isActive ? { attribute: RESTORE_ATTRIBUTE, value: position.name ?? '' } : null
      appendBadge(
        svg,
        { x: point.x + offset.x, y: point.y + offset.y },
        position.type,
        IChangeType.Removed,
        color,
        target,
        !isActive,
      )
    })
}

const overlayMoved = (
  svg: SVGSVGElement,
  baseSvg: SVGSVGElement,
  options: RevisionPaintOptions,
  offset: { x: number; y: number },
) => {
  const { positions, base, movedNames, activeType, ghostColor, colors, publishedNameOf } = options
  if (!base || movedNames.size === 0 || activeType !== IPositionType.MooringLine) return
  positions
    .filter(p => p.type === IPositionType.MooringLine && movedNames.has(p.name ?? ''))
    .forEach(position => {
      const basePosition = base.positions.find(
        p => p.name === publishedNameOf(position.name) && p.type === position.type,
      )
      if (!basePosition) return
      const newAnchor = pointOf(markPosition(svg, position), position.type)
      drawMoveGhost(svg, markPosition(baseSvg, basePosition), newAnchor, ghostColor, colors[IChangeType.Moved], offset)
    })
}

// Colours added, modified and moved positions, draws removed ones as faded dashed ghosts, and badges every change.
export const paintRevisionDrawing = (svg: SVGSVGElement, options: RevisionPaintOptions) => {
  const { positions, changeByName, colors, palette, base, activeType, skipIds } = options
  const baseSvg = base ? parseSvg(base.svg) : null
  const offset =
    base && baseSvg ? offsetFromBase(svg, baseSvg, positions, base.positions, options.publishedNameOf) : { x: 0, y: 0 }

  if (baseSvg) overlayMoved(svg, baseSvg, options, offset)

  positions.forEach(position => {
    if (position.type !== activeType || skipIds.has(position.id)) return
    // Positions without a drawing keep the map's unconfigured look; their badge still shows the change.
    if (position.positionDrawingId == null) return
    const change = changeByName.get(position.name ?? '')
    if (change === IChangeType.Added || change === IChangeType.Modified || change === IChangeType.Moved) {
      const nodes = markPosition(svg, position)
      const hasLabelBox = nodes.some(node => tagOf(node) === 'rect')
      nodes.forEach(node => styleNode(node as SVGElement, 'configured', colors[change], palette, hasLabelBox))
    }
  })

  if (base && baseSvg && options.removedNames.size > 0)
    overlayRemoved(svg, baseSvg, base.positions, options.removedNames, options, offset)

  drawChangeBadges(svg, { positions, changeByName, colors, activeType })
}
